<script lang="ts" setup>
import base64toblob from 'base64toblob';
import { useField } from 'vee-validate';
import {
  computed,
  inject,
  onMounted,
  onUnmounted,
  reactive,
  ref,
  shallowRef,
  watch,
} from 'vue';
import { Cropper, CropperResult } from 'vue-advanced-cropper';

import ImagePreview from 'lib/components/image/Image.vue';
import { useWangsVueConfig } from 'lib/plugins/WangsVue';
import genPlaceholder, {
  genRandomPlaceholderBg,
} from 'lib/utils/genPlaceholder.util';

import BackgroundImageCropper from './BackgroundImageCropper.vue';
import type {
  Image,
  ImageCompressorEmits,
  ImageCompressorPayload,
  ImageCompressorProps,
} from './ImageCompressor.vue.d';
import ImageInputInfo from './ImageInputInfo.vue';
import Button from '../button/Button.vue';
import ButtonRadio from '../buttonradio/ButtonRadio.vue';
import Dialog from '../dialog/Dialog.vue';
import DialogConfirm from '../dialogconfirm/DialogConfirm.vue';
import FieldWrapper from '../fieldwrapper/FieldWrapper.vue';
import { FieldValidation } from '../form/Form.vue.d';
import Icon from '../icon/Icon.vue';
import InvisibleField from '../invisiblefield/InvisibleField.vue';
import ValidatorMessage from '../validatormessage/ValidatorMessage.vue';

import 'vue-advanced-cropper/dist/style.css';

const props = withDefaults(defineProps<ImageCompressorProps>(), {
  disabled: false,
  compressedBlob: undefined,
  imagePreviewSize: 'big',
  mandatory: false,
  useValidator: false,
  validatorMessage: 'Photo must be uploaded',
  showInfo: true,
  customRequirements: undefined,
  showValidatorMessage: true,
  rounded: false,
  imagePreviewUrl: undefined,
  label: undefined,
  fieldName: 'imageInput',
  fieldInfo: undefined,
  useDeleteButton: true,
  fileType: 'webp',
  type: 'normal',
});

const emit = defineEmits<ImageCompressorEmits>();

const defaultImage = defineModel<number>('defaultImage', { default: 0 });

const ImagePreset = inject<Record<string, any>>('preset', {}).image;

const { locale } = useWangsVueConfig('ImageCompressor');

onMounted(async () => {
  setField();
  assignPreviewImagesFromProp();
});

onUnmounted(() => {
  revokeObjectURL(0);
  revokeObjectURL(1);
});

const image = ref<Image[]>([
  {
    src: null,
    type: null,
  },
  {
    src: null,
    type: null,
  },
]);

const randomBg = genRandomPlaceholderBg();
const canvasPlaceholder = ref<HTMLCanvasElement>();

const canvas = ref<HTMLCanvasElement>();
const previewImages = ref<(string | Blob | undefined)[]>([]);
const inputImageFile = ref<HTMLInputElement>();
const inputImageFileKey = shallowRef<number>(0);
const vueAdvanceCropper = ref<typeof Cropper>();
// Store the promise resolve function from loadImage function that will be called on applyImageCropper
const resolveFn = ref<(value: void | PromiseLike<void>) => void>();
const currentCropingImageIndex = shallowRef<number>();
const field = reactive<
  FieldValidation<File | File[] | string | string[] | undefined>
>({
  value: props.multiple ? [] : undefined,
});

const invalidInput = shallowRef<boolean>(false);
const invalidMessage = shallowRef<string>();

const inputImageKey = shallowRef(0);
const showImageCropper = shallowRef(false);
const showDeleteConfirm = ref<boolean[]>([false, false]);

// Default value for formattedFileType is 'image/webp'
const formattedFileType = computed<
  ImageCompressorPayload['compressed']['type']
>(() => `image/${props.fileType}`);

const isInitialImage = (index: number): boolean => {
  return (
    typeof previewImages.value[index] == 'string' &&
    previewImages.value[index].includes('data:image/png;base64')
  );
};

const thumbnailSize = computed(() => {
  const sizes = {
    big: 125,
    medium: 80,
    small: 30,
  };

  return props.type === 'inline-table'
    ? sizes.small
    : sizes[props.imagePreviewSize] || sizes.small;
});

const assignPreviewImagesFromProp = async (): Promise<void> => {
  if (props.imagePreviewUrl?.length) {
    previewImages.value = Array.isArray(props.imagePreviewUrl)
      ? props.imagePreviewUrl
      : [props.imagePreviewUrl];
  } else if (Array.isArray(props.compressedBlob)) {
    previewImages.value = props.compressedBlob;
  } else if (props.compressedBlob) {
    previewImages.value = [props.compressedBlob];
  } else if (props.initialName) {
    canvasPlaceholder.value = genPlaceholder(props.initialName, 125, randomBg);
    previewImages.value = [canvasPlaceholder.value?.toDataURL()];
  } else {
    previewImages.value = [];
  }

  if (props.imagePreviewUrl) {
    field.value = props.imagePreviewUrl;
  } else if (props.initialName) {
    field.value = await canvasToFile();
  }
  emit('applyProp');
};

const canvasToFile = async (): Promise<File> => {
  return new Promise((resolve) => {
    canvasPlaceholder.value?.toBlob((blob) => {
      resolve(
        new File([blob ?? new Blob()], Date.now() + '.png', {
          type: 'image/png',
        }),
      );
    });
  });
};

/**
 * Step 1: User select an image from device.
 */
const pickImage = (addPhoto = false): void => {
  if (props.disabled) return;
  if (addPhoto) currentCropingImageIndex.value = 1;
  inputImageFile.value?.click();
};

const handleImageSelected = async (event: Event): Promise<void> => {
  // Get the image file selected.
  const { files } = event.target as HTMLInputElement;

  const imageFiles = Array.from(files ?? []).slice(
    0,
    previewImages.value.length ? 1 : 2,
  );

  for (const [index, file] of imageFiles.entries()) {
    try {
      const imageIndex = currentCropingImageIndex.value ?? index;

      await loadImage(file, imageIndex); // Wait for each image to load
    } catch (error) {
      console.error('Error loading image:', error);
    } finally {
      currentCropingImageIndex.value = undefined;
    }
  }
};

/**
 * Step 2: On 'change' event, load the image to image cropper.
 */
const loadImage = async (file: File, index: number): Promise<void> => {
  const isImageType = (type: string): boolean => {
    return type.includes('image');
  };

  const isExceededLimit = (size: number): boolean => size > 1000000;

  return new Promise((resolve, reject) => {
    // Ensure that you have a file before attempting to read it
    if (file) {
      resetCropper(index);

      // Check file type
      if (!isImageType(file.type)) {
        invalidInput.value = true;
        invalidMessage.value =
          typeof props.validatorMessage !== 'string' &&
          props.validatorMessage?.invalidFormat
            ? props.validatorMessage.invalidFormat
            : 'Tipe file harus berupa gambar!';
        /*
         * Preview.value = { message: 'Tipe file harus berupa gambar!' };
         * imageUploadErrorMessage.value = preview.value.message;
         */
        assignPreviewImagesFromProp();
        showImageCropper.value = false;
        revokeObjectURL(index);
        reject('Tipe file harus berupa gambar!');
      } else if (isExceededLimit(file.size)) {
        invalidInput.value = true;
        invalidMessage.value =
          typeof props.validatorMessage !== 'string' &&
          props.validatorMessage?.exceed
            ? props.validatorMessage.exceed
            : 'Ukuran gambar terlalu besar! Maks. 1 MB';
        assignPreviewImagesFromProp();
        showImageCropper.value = false;
        revokeObjectURL(index);
        reject('Ukuran gambar terlalu besar! Maks. 1 MB');
      } else {
        // 1. Revoke the object URL, to allow the garbage collector to destroy the uploaded before file
        revokeObjectURL(index);

        // 2. Create the blob link to the file to optimize performance:
        const blob = URL.createObjectURL(file);

        /*
         * 3. The steps below are designated to determine a file mime type to use it during the
         * getting of a cropped image from the canvas. You can replace it them by the following string,
         * but the type will be derived from the extension and it can lead to an incorrect result:
         *
         * this.image = {
         *    src: blob;
         *    type: file.type
         * }
         */
        // Create a new FileReader to read this image binary data
        const reader = new FileReader();

        // Define a callback function to run, when FileReader finishes its job
        reader.onload = (e): void => {
          // Set the image source (it will look like blob:http://example.com/2c5270a5-18b5-406e-a4fb-07427f5e7b94)
          image.value[index].src = blob;
          // Determine the image type to preserve it during the extracting the image from canvas:
          image.value[index].type = getMimeType(
            e?.target?.result as ArrayBuffer,
            file.type,
          );
        };

        reader.onerror = (e): void => {
          console.error('🚀 ~ returnnewPromise ~ e:', e);
        };

        // Start the reader job - read file as a data url (base64 format)
        reader.readAsArrayBuffer(file);

        // Adjust photo in dialog

        if (currentCropingImageIndex.value == null)
          currentCropingImageIndex.value = index;
        if (!showImageCropper.value) showImageCropper.value = true;

        invalidInput.value = false;
        invalidMessage.value = undefined;
      }
    }

    inputImageKey.value += 1;

    resolveFn.value = resolve;
  });
};

/**
 * Set field for validation using vee-validate if props.useValidator true.
 * This will store the image to the form values.
 */
const setField = (): void => {
  if (props.useValidator) {
    Object.assign(
      field,
      useField(props.fieldName, (value: string | Blob) => {
        if (!value && props.mandatory) {
          if (
            typeof props.validatorMessage !== 'string' &&
            props.validatorMessage?.empty
          ) {
            return props.validatorMessage.empty;
          }

          return (props.label ?? 'Photo') + ' must be uploaded';
        } else if (props.invalid && props.validatorMessage) {
          return props.validatorMessage as string;
        } else if (invalidMessage.value) {
          return invalidMessage.value;
        }

        return true;
      }),
    );
  }
};

const resetCropper = (index: number): void => {
  image.value[index] = {
    src: null,
    type: null,
  };
};

// This function is used to detect the actual image type,
const getMimeType = (
  file: ArrayBuffer | null | undefined,
  fallback: string | null = null,
): string | null => {
  if (file) {
    const byteArray = new Uint8Array(file).subarray(0, 4);
    let header = '';

    for (const element of byteArray) {
      header += element.toString(16);
    }

    switch (header) {
      case '89504e47':
        return 'image/png';
      case '47494638':
        return 'image/gif';
      case 'ffd8ffe0':
      case 'ffd8ffe1':
      case 'ffd8ffe2':
      case 'ffd8ffe3':
      case 'ffd8ffe8':
        return formattedFileType.value;
      default:
        return fallback;
    }
  }

  return null;
};

// Convert Base64 to Blob
const toBlobObjectURL = (imgUrl: string): string => {
  const blob = base64toblob(imgUrl.split(',')[1], formattedFileType.value);
  const url = window.URL.createObjectURL(blob);
  return url;
};

/**
 * Convert Blob To File
 * const buildFile = (blob: string, name: string): File =>
 *   new File([blob], name, { type: formattedFileType.value });
 */
const blobToFile = async (url: string, name: string): Promise<File> => {
  const blobFile = await fetch(url).then((f) => f.blob());
  return new File([blobFile], name, { type: formattedFileType.value });
};

/**
 * Step 3: After user finally crop the image, we will get the result from the cropper
 * and then we will compress the image with the help of canvas and then we will
 * get the compressed image in base64 format.
 */
const applyImageCropper = async (): Promise<void> => {
  const currentImageIndex = currentCropingImageIndex.value ?? 0;

  try {
    await cropImage(currentImageIndex);
  } catch (error) {
    console.error(error);
  }

  closeImageCropper();
};

const closeImageCropper = (): void => {
  showImageCropper.value = false;
  resolveFn.value?.();
  resolveFn.value = undefined;
};

const cropImage = async (index: number): Promise<void> => {
  if (vueAdvanceCropper.value) {
    const croppedImage = vueAdvanceCropper.value.getResult() as CropperResult;

    if (croppedImage.canvas) {
      const base64 = croppedImage.canvas.toDataURL(formattedFileType.value);
      await drawCroppedImage(base64, index);
    }
  }
};

const drawCroppedImage = async (
  imageDataURL: string,
  index: number,
): Promise<void> => {
  try {
    // Recreate Canvas Element
    canvas.value = document.createElement('canvas');

    // Set Canvas Context
    const canvasContext = canvas.value.getContext('2d');

    // Create New Image
    const newImage = new Image();
    newImage.src = imageDataURL;
    await newImage.decode();

    // Image Size After Scaling
    const formattedScale = 80 / 100;
    const width = newImage.width * formattedScale;
    const height = newImage.height * formattedScale;

    // Set Canvas Height And Width According to Image Size And Scale
    canvas.value.setAttribute('width', width.toString());
    canvas.value.setAttribute('height', height.toString());

    if (canvasContext) canvasContext.drawImage(newImage, 0, 0, width, height);

    // If all files have been proceed
    const base64 = canvas.value.toDataURL(formattedFileType.value, 0.95);
    const fileName = Date.now() + `.${props.fileType}`;

    const imageCompressorPayload: ImageCompressorPayload = {
      canvas: canvas.value,
      compressed: {
        blob: toBlobObjectURL(base64),
        base64,
        fileName,
        file: await blobToFile(toBlobObjectURL(base64), fileName),
        size: '',
        type: formattedFileType.value,
      },
      message: 'File has been compressed.',
    };

    imageCompressorPayload.compressed.size =
      Math.round(imageCompressorPayload.compressed.file.size / 1000) + 'KB';

    previewImages.value[index] = imageCompressorPayload.compressed.blob;

    if (props.multiple) {
      if (field.value && Array.isArray(field.value)) {
        field.value[index] = imageCompressorPayload.compressed.file;
      } else {
        field.value = [imageCompressorPayload.compressed.file];
      }
    } else {
      field.value = imageCompressorPayload.compressed.file;
    }

    emit('apply', imageCompressorPayload);
  } catch (error) {
    console.error(error);
  }
};

const revokeObjectURL = (index: number): void => {
  // Revoke the object URL, to allow the garbage collector to destroy the uploaded before file
  if (image.value[index].src) URL.revokeObjectURL(image.value[index].src);
};

const onBeforeDeleteImage = (index: number): void => {
  if (props.confirmOnDelete) {
    showDeleteConfirm.value[index] = true;
  } else if (props.emitDeleteFn) {
    emit('delete', deleteImage, index);
  } else deleteImage(index);
};

const deleteImage = (index = 0): void => {
  invalidInput.value = false;
  invalidMessage.value = undefined;
  previewImages.value = previewImages.value.toSpliced(index, 1);

  revokeObjectURL(index);

  // Swap the image index, because when an image deleted, the second image become the first
  image.value[index] = { src: null, type: null };
  image.value = [image.value[+!index], image.value[index]];

  currentCropingImageIndex.value = 0;
  defaultImage.value = 0;

  if (!props.confirmOnDelete) emit('delete');
  if (props.multiple && Array.isArray(field.value)) {
    field.value = field.value.toSpliced(index, 1);
  } else if (field.handleReset) field.handleReset();
};

/**
 * On button edit clicked, checks wether the previewImages is Blob file from user's device,
 * if so, open the cropper again.
 *
 * Otherwise, open the file manager to allow user pick an image.
 */
const editImage = (index: number): void => {
  const currentPreviewImage = previewImages.value[index]?.toString();
  currentCropingImageIndex.value = index;

  if (currentPreviewImage?.includes('blob')) {
    currentCropingImageIndex.value = index;
    showImageCropper.value = true;
  } else {
    resetCropper(index);
    pickImage();
  }
};

/**
 * When user wants to change the image already picked.
 */
const changeImage = (): void => {
  pickImage();
};

const isChromeBased = computed(() => {
  return navigator.userAgent.includes('Chrome');
});

watch(
  () => field.value,
  (value: File[] | File | undefined) => {
    if (
      value instanceof File ||
      (Array.isArray(value) && value.some((v) => v instanceof File))
    ) {
      emit('update:fieldValue', value);
    }
  },
);

watch(invalidMessage, (value) => {
  if (value && field.setErrors) field.setErrors(value);
});

watch(showImageCropper, (value) => {
  if (!value) inputImageFileKey.value++;
});

watch(
  (): string | undefined => props.initialName,
  () => {
    assignPreviewImagesFromProp();
  },
);

watch(
  (): string | string[] | undefined => props.imagePreviewUrl,
  () => {
    assignPreviewImagesFromProp();
  },
  { once: true },
);

defineExpose({ assignPreviewImagesFromProp });
</script>

<template>
  <FieldWrapper
    :class="[{ '!w-max': !props.multiple, '!w-full': props.multiple }]"
    :info="props.fieldInfo"
    :label="props.label"
    :mandatory="props.mandatory"
    data-wv-name="image-compressor"
    data-wv-section="root"
  >
    <div
      :class="[
        'w-full',
        {
          'grid gap-6 items-start grid-cols-2': multiple && type === 'normal',
          'grid gap-3 items-start grid-cols-[max-content,max-content]':
            multiple && type === 'inline-table',
        },
      ]"
      data-wv-section="wrapper"
    >
      <template v-if="previewImages.length">
        <div
          :key="previewImage.toString()"
          v-for="(previewImage, index) of previewImages"
          :class="[
            {
              'items-start': type === 'normal',
              'items-center': type === 'inline-table',
            },
          ]"
          class="flex gap-1"
          data-wv-section="preview"
        >
          <div
            :class="[
              'flex gap-1 items-center',
              {
                'max-w-[80px]': imagePreviewSize === 'medium',
                'max-w-[125px]': imagePreviewSize === 'big',
                'flex-col': type === 'normal',
              },
            ]"
            data-wv-section="preview-image-wrapper"
          >
            <div
              :class="[
                {
                  '[&>span]:hover:opacity-30 [&>[data-wv-section=input-image-trigger]]:hover:opacity-100':
                    isInitialImage(index),
                  'w-[125px] h-[125px]':
                    imagePreviewSize === 'big' && type === 'normal',
                  'w-[30px] h-[30px]':
                    imagePreviewSize === 'small' || type === 'inline-table',
                  'w-[80px] h-[80px]':
                    imagePreviewSize === 'medium' && type === 'normal',
                },
                'relative',
              ]"
              data-wv-section="image-wrapper"
            >
              <ImagePreview
                :class="{ 'w-[30px] h-[30px]': type === 'inline-table' }"
                :rounded="rounded"
                :src="previewImage"
                :thumbnail-size="thumbnailSize"
                :use-default-size="false"
                class="transition-opacity duration-300"
                data-wv-section="preview-image"
              />

              <button
                v-if="isInitialImage(index)"
                :class="[
                  ImagePreset?.button.class,
                  'hover:bg-opacity-0 rounded-lg opacity-0',
                ]"
                @click="pickImage(false)"
                data-wv-section="input-image-trigger"
                type="button"
              >
                <Icon
                  class="w-6 h-6"
                  data-wv-section="trigger-icon"
                  icon="image-add"
                  severity="primary"
                />
              </button>
            </div>

            <template v-if="!props.disabled && !isInitialImage(index)">
              <div
                :class="[
                  'flex gap-0.5 justify-center',
                  { 'scale-75': imagePreviewSize === 'medium' },
                ]"
                data-wv-section="preview-buttons"
              >
                <template v-if="type === 'normal'">
                  <Button
                    @click="editImage(index)"
                    class="!py-0 !px-1 !h-max"
                    icon="pencil"
                    label="Edit"
                    text
                  />

                  <Button
                    v-if="useDeleteButton"
                    @click="onBeforeDeleteImage(index)"
                    class="!py-0 !px-1 !h-max"
                    icon="delete-bin"
                    label="Delete"
                    severity="danger"
                    text
                  />
                </template>

                <template v-else>
                  <Icon
                    @click="editImage(index)"
                    class="text-xs p-0.5 rounded-xs mr-1"
                    icon="pencil"
                    severity="primary"
                  />

                  <Icon
                    @click="onBeforeDeleteImage(index)"
                    class="text-xs p-0.5 rounded-xs"
                    icon="delete-bin"
                    severity="danger"
                  />
                </template>
              </div>

              <DialogConfirm
                v-model:visible="showDeleteConfirm[index]"
                :closable="false"
                @confirm="emit('delete', deleteImage, index)"
                body="Are you sure you want to delete it?"
                confirm-label="Delete"
                header="Delete Photo"
                severity="danger"
              />
            </template>
          </div>

          <ImageInputInfo
            v-if="type === 'normal'"
            :custom-requirements="customRequirements"
            :show-add-button="previewImages.length === 1 && props.multiple"
            :show-info="showInfo"
            @add-photo="pickImage(true)"
          >
            <ButtonRadio
              v-if="previewImages.length === 2 && props.multiple"
              v-model="defaultImage"
              :label-id="'default-' + index"
              :value="index"
              label="Set as default"
            />
          </ImageInputInfo>

          <Button
            v-if="
              multiple && previewImages.length == 1 && type === 'inline-table'
            "
            @click="pickImage(true)"
            icon="add"
            label="Photo"
            text
          />
        </div>
      </template>

      <template v-else-if="type != 'inline-table'">
        <div class="flex gap-1 items-start">
          <button
            :class="[
              {
                'w-[125px] h-[125px]': imagePreviewSize === 'big',
                'w-[30px] h-[30px]': imagePreviewSize === 'small',
                'w-[80px] h-[80px]': imagePreviewSize === 'medium',
                'ring-1': !isChromeBased,
                'ring-[0.5px]': isChromeBased,
                'rounded-full': rounded,
                'rounded-lg': !rounded,
                'ring-general-400 dark:ring-general-400 focus-visible:ring-primary-500 dark:focus-visible:ring-primary-500':
                  !invalidInput && !field.errorMessage,
                'ring-danger-500 dark:ring-danger-500':
                  invalidInput || field.errorMessage,
                'bg-white dark:bg-grayscale-900': !props.disabled,
                'bg-general-50 dark:bg-grayscale-800': props.disabled,
              },
              'flex items-center justify-center',
              'ring-inset',
              'cursor-pointer',
            ]"
            @click="pickImage(false)"
            data-wv-section="input-image-trigger"
            type="button"
          >
            <Icon
              class="w-6 h-6"
              data-wv-section="trigger-icon"
              icon="image-add"
              severity="primary"
            />
          </button>

          <ImageInputInfo
            :custom-requirements="customRequirements"
            :show-info="showInfo"
          />
        </div>
      </template>
    </div>

    <input
      :id="fieldName"
      :key="inputImageFileKey"
      ref="inputImageFile"
      :multiple="props.multiple && !previewImages.length"
      @change="handleImageSelected"
      accept="image/*"
      class="hidden"
      data-wv-section="hidden-image-input"
      type="file"
    />

    <label
      v-if="type === 'inline-table' && !previewImages.length"
      :for="fieldName"
      class="inline-flex items-center cursor-pointer"
    >
      <span
        class="inline-flex items-center text-body-medium rounded-bl rounded-tl bg-general-50 dark:bg-grayscale-800 px-3 h-[26px] border-[0.5px] border-general-200 dark:border-grayscale-800"
      >
        Browse
      </span>

      <span
        class="inline-flex items-center text-body-medium !text-general-200 dark:!text-general-600 !font-light rounded-br rounded-tr px-3 h-[26px] leading-4 border-[0.5px] border-l-0 border-general-200 dark:border-grayscale-800"
      >
        Select File
      </span>
    </label>

    <ValidatorMessage
      v-if="showValidatorMessage"
      :format="false"
      :message="field.errorMessage ?? invalidMessage"
    />

    <InvisibleField
      v-if="props.multiple"
      :value="defaultImage ? 'secondImage' : 'firstImage'"
      field-name="defaultImage"
    />
  </FieldWrapper>

  <Dialog
    v-model:visible="showImageCropper"
    :draggable="false"
    :header="locale.cropDialogHeader"
    class="w-max max-w-[98vw] min-w-[min(500px,98vw)] !max-h-[94vh]"
    content-class="overflow-hidden overflow-y-auto min-h-[100px]"
    data-wv-section="dialog-cropper"
    footer-class="!gap-1"
    modal
  >
    <Cropper
      ref="vueAdvanceCropper"
      v-if="currentCropingImageIndex != null"
      :auto-zoom="false"
      :background-wrapper-component="BackgroundImageCropper"
      :src="image[currentCropingImageIndex].src"
      class="upload-example-cropper h-400"
      foreground-class="h-400"
      image-class="h-400"
    />

    <template #footer>
      <Button
        :label="locale.cancelCropLabel"
        @click="closeImageCropper"
        data-wv-section="cancel-crop"
        severity="secondary"
        text
        type="button"
      />

      <Button
        :label="locale.changeImageLabel"
        @click="changeImage"
        data-wv-section="change-image-button"
        text
        type="button"
      />

      <Button
        :label="locale.applyCropLabel"
        @click="applyImageCropper"
        data-wv-section="apply-image-button"
        severity="success"
        type="button"
      />
    </template>
  </Dialog>
</template>
